import { http } from "@/composable/httpService";
import { useHandleErrorStatus } from "@/composable/useHandleErrorStatus";
import axios, { AxiosError } from "axios";
import { defineStore } from "pinia";

const SORT_PARAMS = {
  newest: { sortBy: "created_at", sortOrder: "desc" },
  oldest: { sortBy: "created_at", sortOrder: "asc" },
  "name-asc": { sortBy: "name", sortOrder: "asc" },
  "name-desc": { sortBy: "name", sortOrder: "desc" },
};

export const useMyVideosStore = defineStore("my-videos.store", {
  state: () => ({
    loadingMyVideos: false,
    loadingDelete: false,
    controller: null,
    currentPage: 1,
    perPage: 12,
    sortSelection: "newest",
    myVideos: [],
    totalRecords: 0,
  }),
  getters: {
    sorts() {
      return [
        { name: "Newest First", value: "newest" },
        { name: "Oldest First", value: "oldest" },
        { name: "Name A-Z", value: "name-asc" },
        { name: "Name Z-A", value: "name-desc" },
      ];
    },
    myVideosParams(state) {
      return {
        page: state.currentPage,
        perPage: state.perPage,
        videoType: "uploaded",
        ...(SORT_PARAMS[state.sortSelection] ?? SORT_PARAMS.newest),
      };
    },
    hasMore(state) {
      return state.myVideos.length < state.totalRecords;
    },
  },
  actions: {
    async getMyVideos({ append = false } = {}) {
      try {
        this.loadingMyVideos = true;
        this.controller?.abort();
        this.controller = new AbortController();
        const { data } = await http().get("v3/units", {
          signal: this.controller.signal,
          params: this.myVideosParams,
        });
        this.myVideos = append ? [...this.myVideos, ...data.data] : data.data;
        this.totalRecords = data.total;
      } catch (error) {
        if (axios.isCancel(error)) return;
        if (!(error instanceof AxiosError)) throw error;
        useHandleErrorStatus(error);
      } finally {
        this.loadingMyVideos = false;
      }
    },
    async loadMoreMyVideos() {
      if (!this.hasMore || this.loadingMyVideos) return;
      this.currentPage += 1;
      await this.getMyVideos({ append: true });
    },
    resetMyVideosList() {
      this.currentPage = 1;
      this.myVideos = [];
      this.totalRecords = 0;
    },
    async handleDeleteMyVideo(id) {
      try {
        this.loadingDelete = true;
        await http().delete(`/units/${id}`);
        this.myVideos = this.myVideos.filter((video) => video.id !== id);
        this.totalRecords = Math.max(0, this.totalRecords - 1);
      } catch (error) {
        if (!(error instanceof AxiosError)) throw error;
        useHandleErrorStatus(error);
        throw error;
      } finally {
        this.loadingDelete = false;
      }
    },
    abortMyVideosRequest() {
      this.controller?.abort();
    },
  },
});
